import {usePortalTooltip} from '@github-ui/portal-tooltip/use-portal-tooltip'
import {testIdProps} from '@github-ui/test-id-props'
import {FocusKeys} from '@primer/behaviors'
import {ChevronLeftIcon, ChevronRightIcon, SlidersIcon, ZoomInIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton, Popover, useFocusZone} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useEffect, useId, useMemo, useRef, useState} from 'react'

import {ViewOptionsToolbarUI} from '../../../api/stats/contracts'
import {PotentiallyDirty} from '../../../components/potentially-dirty'
import {RoadmapDateFieldsMenu} from '../../../components/roadmap/roadmap-date-fields-menu'
import {MarkerDirtyIcon, RoadmapMarkerMenu} from '../../../components/roadmap/roadmap-marker-menu'
import {RoadmapZoomLevelMenu} from '../../../components/roadmap/roadmap-zoom-level-menu'
import {SortByMenu} from '../../../components/sort-by-menu'
import {optionsForRoadmapToolbar} from '../../../components/view-options/configuration'
import {RoadmapDateFieldsDirtyIcon, SortLeadingVisual} from '../../../components/view-options/icons'
import {useRoadmapDateFieldsMenu} from '../../../features/roadmap/roadmap-date-fields-menu'
import {useRoadmapDateFieldsPopover} from '../../../features/roadmap/use-roadmap-date-fields-popover'
import {ViewerPrivileges} from '../../../helpers/viewer-privileges'
import {usePrefixedId} from '../../../hooks/common/use-prefixed-id'
import {useRoadmapSettings, useRoadmapZoomLevel} from '../../../hooks/use-roadmap-settings'
import {useRoadmapTableIsNarrow} from '../../../hooks/use-roadmap-table-is-narrow'
import {useSortedBy} from '../../../hooks/use-sorted-by'
import {ViewOptionsStatsUiContext} from '../../../hooks/use-view-options-stats-ui-key'
import {usePrefetchIssueFieldsQuery} from '../../../state-providers/issue-fields/use-issue-fields-query'
import {RoadmapResources} from '../../../strings'
import {useRoadmapNavigation, useRoadmapView} from '../roadmap-view-provider'
import styles from './roadmap-controls.module.css'

/** Roadmap header controls including navigation, zoom level, and other options */
export const RoadmapControls = memo(function RoadmapControls() {
  const containerRef = useRef<HTMLUListElement>(null)
  useFocusZone({
    containerRef,
    bindKeys: FocusKeys.ArrowHorizontal,
  })
  const {isTableNarrow} = useRoadmapTableIsNarrow()
  const prefetchIssueFields = usePrefetchIssueFieldsQuery()

  useEffect(() => {
    prefetchIssueFields()
  }, [prefetchIssueFields])

  return (
    <ul
      role="menubar"
      ref={containerRef}
      className={clsx(styles.controlsToolbar)}
      {...testIdProps(`quick-action-toolbar`)}
    >
      <ViewOptionsStatsUiContext.Provider value={ViewOptionsToolbarUI}>
        {!isTableNarrow && (
          <>
            <RoadmapMarkerButton />
            <RoadmapSortByMenu />
            <DateConfigurationMenu />
            <ZoomLevelMenu />
          </>
        )}
        {isTableNarrow && <RoadmapCondensedOptions />}
        <TodayButton />
        <PrevPageButton />
        <NextPageButton />
      </ViewOptionsStatsUiContext.Provider>
    </ul>
  )
})

const RoadmapCondensedOptions = () => {
  const id = useId()
  const anchorRef = useRef<HTMLButtonElement>(null)
  const submenuRef = useRef<HTMLLIElement>(null)

  const [isOpen, setOpen] = useState(false)
  const [submenu, setSubmenu] = useState<string | null>(null)

  const {isSortedByDirty} = useSortedBy()
  const {isRoadmapMarkerFieldsDirty, isRoadmapZoomLevelDirty, isRoadmapDateFieldsDirty} = useRoadmapSettings()
  const showDirtyState =
    isSortedByDirty || isRoadmapMarkerFieldsDirty || isRoadmapZoomLevelDirty || isRoadmapDateFieldsDirty
  const {hasWritePermissions} = ViewerPrivileges()

  const getAnchor = (name: string) => {
    // Allow anchoring the "Add Field" overlay to the menu anchor
    if (name === 'Dates' && submenu !== 'Dates') {
      return anchorRef
    }
    return submenuRef
  }

  return (
    <>
      <li role="menuitem">
        <PotentiallyDirty
          {...testIdProps('quick-action-toolbar-options-dirty')}
          isDirty={showDirtyState}
          hideDirtyState={!hasWritePermissions}
        >
          <IconButton
            aria-label={RoadmapResources.toolbarRoadmapOptions}
            ref={anchorRef}
            size="small"
            variant="invisible"
            icon={SlidersIcon}
            onClick={() => setOpen(!isOpen)}
            aria-controls={id}
            aria-expanded={isOpen}
            aria-haspopup="true"
            className={styles.toolbarButton}
            {...testIdProps('quick-action-toolbar-options-button')}
          />
        </PotentiallyDirty>
      </li>
      <ActionMenu anchorRef={anchorRef} open={isOpen} onOpenChange={nextOpen => setOpen(nextOpen)}>
        <ActionMenu.Overlay
          variant={{
            regular: 'anchored',
            narrow: 'fullscreen',
          }}
          {...testIdProps('quick-action-toolbar-options-menu')}
        >
          <ActionList id={id}>
            {optionsForRoadmapToolbar.map(option => (
              <ActionList.Item
                ref={submenu === option.title ? submenuRef : null}
                key={option.title}
                onSelect={e => {
                  setSubmenu(option.title)
                  e.preventDefault()
                }}
              >
                <ActionList.LeadingVisual>
                  <option.icon />
                </ActionList.LeadingVisual>
                <option.TextContent />
                <ActionList.TrailingVisual>
                  <ChevronRightIcon />
                </ActionList.TrailingVisual>
              </ActionList.Item>
            ))}
          </ActionList>
          {optionsForRoadmapToolbar.map(option => (
            <option.MenuComponent
              key={option.title}
              anchorRef={getAnchor(option.title)}
              open={submenu === option.title}
              setOpen={nextOpen => {
                setSubmenu(nextOpen ? option.title : null)
              }}
            />
          ))}
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}

const RoadmapMarkerButton = () => {
  const id = useId()
  const ref = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  return (
    <li role="menuitem" className={styles.menuItem}>
      <Button
        {...testIdProps('quick-action-toolbar-markers-menu')}
        ref={ref}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={id}
        trailingVisual={null}
        variant="invisible"
        size="small"
        leadingVisual={MarkerDirtyIcon}
        onClick={() => setOpen(s => !s)}
        className={styles.toolbarButton}
      >
        Markers
      </Button>
      <ActionMenu anchorRef={ref} open={open}>
        <ActionMenu.Overlay
          role="dialog"
          onEscape={() => setOpen(false)}
          onClickOutside={() => setOpen(false)}
          aria-label="Configure vertical markers to display on roadmap"
        >
          <RoadmapMarkerMenu id={id} />
        </ActionMenu.Overlay>
      </ActionMenu>
    </li>
  )
}

function TodayButton() {
  const {today} = useRoadmapView()
  const {scrollToDate} = useRoadmapNavigation()
  const handleClick = () => {
    scrollToDate(today, true)
  }
  const button = useRef<HTMLButtonElement>(null)
  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: button,
    'aria-label': RoadmapResources.scrollToDateText(today),
  })
  return (
    <li role="menuitem" className={styles.menuItem}>
      <Button
        onClick={handleClick}
        size="small"
        variant="invisible"
        {...contentProps}
        ref={button}
        className={styles.toolbarButton}
      >
        {RoadmapResources.toolbarToday}
      </Button>
      {tooltip}
    </li>
  )
}

function PrevPageButton() {
  const {scrollToPrevPage} = useRoadmapNavigation()
  const handleClick = () => {
    scrollToPrevPage(true)
  }

  return (
    <li role="menuitem">
      <IconButton
        tooltipDirection="nw"
        onClick={handleClick}
        size="small"
        variant="invisible"
        aria-label={RoadmapResources.scrollToPreviousPage}
        icon={ChevronLeftIcon}
        className={styles.toolbarButton}
      />
    </li>
  )
}

function NextPageButton() {
  const {scrollToNextPage} = useRoadmapNavigation()
  const handleClick = () => {
    scrollToNextPage(true)
  }

  return (
    <li role="menuitem">
      <IconButton
        role="menuitem"
        tooltipDirection="nw"
        onClick={handleClick}
        size="small"
        variant="invisible"
        aria-label={RoadmapResources.scrollToNextPage}
        icon={ChevronRightIcon}
        className={styles.toolbarButton}
      />
    </li>
  )
}

function RoadmapSortByMenu() {
  const {isSorted, sorts} = useSortedBy()
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  const sortByButtonOnClick = () => {
    setOpen(!open)
  }

  const {sortLabel, sortText} = useMemo(() => {
    if (isSorted) {
      return {
        // For example: "Sorted by: Status ascending, Title descending"
        sortLabel: `Sorted by: ${sorts
          .map(sort => `${sort.column.name} ${sort.direction === 'asc' ? 'ascending' : 'descending'}`)
          .join(', ')}`,
        sortText: sorts.map(sort => sort.column.name).join(', '),
      }
    }
    return {
      sortLabel: `No sort applied`,
      sortText: 'Sort',
    }
  }, [isSorted, sorts])

  const menuId = usePrefixedId('quick-action-toolbar-sort-by-menu')

  return (
    <li role="menuitem" className={styles.menuItem}>
      <Button
        ref={anchorRef}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={sortByButtonOnClick}
        variant="invisible"
        size="small"
        aria-controls={menuId}
        aria-label={sortLabel}
        className={styles.toolbarButton}
        {...testIdProps('quick-action-toolbar-sort-by-menu')}
      >
        <div className={styles.buttonContent}>
          <SortLeadingVisual />
          <span className={styles.buttonText}>{sortText}</span>
        </div>
      </Button>
      <SortByMenu id={menuId} anchorRef={anchorRef} open={open} setOpen={setOpen} />
    </li>
  )
}

function ZoomLevelMenu() {
  const {hasWritePermissions} = ViewerPrivileges()
  const zoomLevel = useRoadmapZoomLevel()
  const {isRoadmapZoomLevelDirty} = useRoadmapSettings()

  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  const zoomLevelButtonOnClick = () => {
    setOpen(!open)
  }

  const menuId = usePrefixedId('roadmap-zoom-level-menu')

  return (
    <li role="menuitem" className={styles.menuItem}>
      <Button
        ref={anchorRef}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={zoomLevelButtonOnClick}
        variant="invisible"
        size="small"
        aria-label={RoadmapResources.toolbarZoomAriaLabel(zoomLevel)}
        aria-controls={menuId}
        className={styles.toolbarButton}
        {...testIdProps('quick-action-toolbar-zoom-level')}
      >
        <div className={styles.buttonContent}>
          <PotentiallyDirty
            isDirty={isRoadmapZoomLevelDirty}
            hideDirtyState={!hasWritePermissions}
            {...testIdProps('roadmap-zoom-level-dirty')}
          >
            <ZoomInIcon />
          </PotentiallyDirty>
          <span className={styles.buttonText}>{RoadmapResources.zoomLevelOptions[zoomLevel]}</span>
        </div>
      </Button>
      <RoadmapZoomLevelMenu id={menuId} anchorRef={anchorRef} open={open} setOpen={setOpen} />
    </li>
  )
}

function DateConfigurationMenu() {
  const {isDateConfigurationMenuOpen, setOpenDateConfigurationMenu} = useRoadmapDateFieldsMenu()
  const {
    showDateFieldsPopover,
    showInitialSelectedFieldsMessage,
    initialFieldType,
    handleDateFieldsPopoverPrimaryAction,
  } = useRoadmapDateFieldsPopover()

  const anchorRef = useRef<HTMLButtonElement>(null)

  const datesButtonOnClick = () => {
    setOpenDateConfigurationMenu(!isDateConfigurationMenuOpen)
  }

  const menuId = usePrefixedId('roadmap-date-fields-menu')

  return (
    <li role="menuitem" className={styles.menuItem}>
      <Button
        ref={anchorRef}
        aria-haspopup="true"
        aria-expanded={isDateConfigurationMenuOpen}
        onClick={datesButtonOnClick}
        variant="invisible"
        size="small"
        aria-label={RoadmapResources.toolbarDateFieldsAriaLabel}
        aria-controls={menuId}
        className={styles.toolbarButton}
        {...testIdProps('quick-action-toolbar-date-fields-menu')}
      >
        <div className={styles.buttonContent}>
          <RoadmapDateFieldsDirtyIcon />
          <span className={styles.buttonText}>{RoadmapResources.toolbarDateFields}</span>
        </div>
      </Button>
      {showDateFieldsPopover && (
        <Popover relative={false} open={showDateFieldsPopover} caret="top" className={styles.gettingStartedPopover}>
          <Popover.Content className={styles.gettingStartedPopoverContent}>
            <div className={styles.popoverTitle}>{RoadmapResources.gettingStarted.popoverTitle}</div>
            <div className={styles.popoverBody}>
              {showInitialSelectedFieldsMessage
                ? RoadmapResources.gettingStarted.fieldsSelectedByDefault(initialFieldType)
                : RoadmapResources.gettingStarted.projectNeedsField}
            </div>
            <Button onClick={handleDateFieldsPopoverPrimaryAction}>
              {RoadmapResources.gettingStarted.popoverDismissButton}
            </Button>
          </Popover.Content>
        </Popover>
      )}
      <RoadmapDateFieldsMenu
        id={menuId}
        anchorRef={anchorRef}
        open={isDateConfigurationMenuOpen}
        setOpen={setOpenDateConfigurationMenu}
      />
    </li>
  )
}

try{ RoadmapControls.displayName ||= 'RoadmapControls' } catch {}
try{ RoadmapCondensedOptions.displayName ||= 'RoadmapCondensedOptions' } catch {}
try{ RoadmapMarkerButton.displayName ||= 'RoadmapMarkerButton' } catch {}
try{ TodayButton.displayName ||= 'TodayButton' } catch {}
try{ PrevPageButton.displayName ||= 'PrevPageButton' } catch {}
try{ NextPageButton.displayName ||= 'NextPageButton' } catch {}
try{ RoadmapSortByMenu.displayName ||= 'RoadmapSortByMenu' } catch {}
try{ ZoomLevelMenu.displayName ||= 'ZoomLevelMenu' } catch {}
try{ DateConfigurationMenu.displayName ||= 'DateConfigurationMenu' } catch {}